import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../redux/store';
import { useSelector } from '../redux/hooks';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 40px;
  background: #ffffff;
`;

const Title = styled.h1`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 32px;
  color: #a13b16;
  text-align: center;
  margin-bottom: 30px;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
`;

const List = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-content: flex-start;
  row-gap: 10px;
`;

const ListItem = styled.div<{ $isHovered: boolean }>`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.$isHovered ? '#f0e6e1' : '#f8f8f8'};
  white-space: nowrap;
`;

const Form = styled.form`
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #a13b16;
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
`;

const DangerButton = styled(Button)`
  background: #c0392b;
`;

const CancelButton = styled(Button)`
  background: #999;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

interface Format {
  format_id: number;
  format: string;
}

const FormatsListView: React.FC = () => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [formats, setFormats] = useState<Format[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<Format | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BASE_API_URL}/formats`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        setFormats(data);
      })
      .catch(err => {
        console.error('Failed to fetch formats:', err);
      });
  }, []);

  const handleSelect = useCallback((format: Format) => {
    setSelectedFormat(format);
    setNameInput(format.format);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!nameInput.trim()) return;

      if (selectedFormat) {
        fetch(
          `${process.env.REACT_APP_BASE_API_URL}/formats/${selectedFormat.format_id}`,
          {
            method: "PUT",
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ format: nameInput })
          }
        )
          .then(res => {
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
          })
          .then(data => {
            setFormats(prev =>
              prev.map(f => data.format_id === f.format_id ? data : f)
            );
          })
          .catch(err => {
            console.error('Failed to PUT format:', err);
          });
      } else {
        fetch(`${process.env.REACT_APP_BASE_API_URL}/formats`, {
          method: "POST",
          credentials: "include",
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ format: nameInput })
        })
          .then(res => {
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
          })
          .then(data => {
            setFormats(prev => [...prev, data]);
          })
          .catch(err => {
            console.error('Failed to POST format:', err);
          });
      }

      setSelectedFormat(null);
      setNameInput('');
    },
    [accessToken, nameInput, selectedFormat]
  );

  const handleDelete = useCallback(() => {
    if (!selectedFormat) return;

    fetch(`${process.env.REACT_APP_BASE_API_URL}/formats/${selectedFormat.format_id}`, {
      method: "DELETE",
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        setFormats(prev => prev.filter(f => f !== selectedFormat));
        return res.json();
      })
      .catch(err => {
        console.error('Failed to DELETE format:', err);
      });

    setSelectedFormat(null);
    setNameInput('');
  }, [accessToken, selectedFormat]);

  const handleCancel = useCallback(() => {
    setSelectedFormat(null);
    setNameInput('');
  }, []);

  return (
    <PageContainer>
      <TopBar>
        <Title>Формати</Title>
        <Button onClick={() => navigate('/admin')}>Головна</Button>
      </TopBar>
      <FlexRow>
        <List>
          {formats.map(format => (
            <ListItem
              key={format.format_id}
              $isHovered={hoveredItem === format.format}
              onClick={() => handleSelect(format)}
              onMouseEnter={() => setHoveredItem(format.format)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {format.format}
            </ListItem>
          ))}
        </List>

        <Form onSubmit={handleSubmit}>
          <Input
            placeholder="Назва формату"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
          />
          <Button type="submit">
            {selectedFormat ? 'Зберегти' : 'Створити'}
          </Button>
          {selectedFormat && (
            <>
              <DangerButton type="button" onClick={handleDelete}>
                Видалити
              </DangerButton>
              <CancelButton type="button" onClick={handleCancel}>
                Відмінити
              </CancelButton>
            </>
          )}
        </Form>
      </FlexRow>
    </PageContainer>
  );
};

export default FormatsListView;